import { useEffect, useState } from "react";
import { mntAPI, fmtINR } from "./mntApi";
import { toast } from "sonner";
import { Plus, FileCheck } from "lucide-react";
import { Modal, F, cls, Footer } from "./MaintenanceWorkOrders";
import { DatePicker } from "@/components/ui/date-picker";
import { NumberInput } from "@/components/ui/number-input";

export default function MaintenanceAMCs() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => mntAPI.amcs().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="mnt-amcs">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[16px] font-semibold text-hocp-navy">Annual Maintenance Contracts</h2>
          <p className="text-[12.5px] text-slate-500">Vendor contracts covering assets, renewals, and expiry tracking.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="mnt-amc-new"
                className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> New AMC
        </button>
      </div>

      {!rows.length ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-[13px] text-slate-500">
          <FileCheck className="w-6 h-6 mx-auto text-slate-300 mb-2"/>
          No AMCs yet. Add one to track vendor coverage and renewals.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(a => {
            const daysLeft = Math.round((new Date(a.end_date) - new Date()) / 86400000);
            const expiring = daysLeft >= 0 && daysLeft <= 60;
            const expired = daysLeft < 0;
            return (
              <div key={a.id} data-testid={`mnt-amc-${a.id}`}
                   className={`rounded-2xl border p-4 ${expired ? "border-rose-200 bg-rose-50/30" : expiring ? "border-amber-200 bg-amber-50/30" : "border-slate-200 bg-white"}`}>
                <div className="flex items-start justify-between mb-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10.5px] mono text-slate-400">{a.code}</div>
                    <div className="font-display text-[15px] font-semibold text-hocp-navy mt-0.5 truncate">{a.title}</div>
                  </div>
                  {expired && <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-rose-100 text-rose-700">Expired</span>}
                  {expiring && !expired && <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-100 text-amber-700">Expiring</span>}
                </div>
                <div className="text-[12px] text-slate-600 space-y-1">
                  <div><span className="text-slate-400">Vendor</span> · {a.vendor_name || "—"}</div>
                  <div><span className="text-slate-400">Period</span> · <span className="mono">{a.start_date} → {a.end_date}</span></div>
                  {a.annual_cost && <div><span className="text-slate-400">Annual</span> · {fmtINR(a.annual_cost)}</div>}
                  {a.coverage && <div><span className="text-slate-400">Coverage</span> · {a.coverage}</div>}
                </div>
                {a.contact_person && (
                  <div className="text-[11.5px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
                    {a.contact_person} {a.phone && <span>· {a.phone}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {creating && <AMCDialog onClose={() => setCreating(false)}
                               onDone={() => { setCreating(false); load(); }}/>}
    </div>
  );
}

function AMCDialog({ onClose, onDone }) {
  const [f, setF] = useState({
    code: "", title: "", vendor_name: "", contact_person: "", phone: "", email: "",
    coverage: "", start_date: new Date().toISOString().slice(0, 10),
    end_date: "", annual_cost: 0,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.code || !f.title || !f.start_date || !f.end_date) {
      toast.error("Code, title, start & end dates are required"); return;
    }
    setSaving(true);
    try {
      await mntAPI.createAMC({ ...f, annual_cost: Number(f.annual_cost) });
      toast.success("AMC created"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New AMC" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <F label="Code"><input value={f.code} onChange={e => setF({...f, code: e.target.value.toUpperCase()})} data-testid="new-amc-code" className={cls}/></F>
        <F label="Title"><input value={f.title} onChange={e => setF({...f, title: e.target.value})} data-testid="new-amc-title" className={cls}/></F>
        <F label="Vendor Name"><input value={f.vendor_name} onChange={e => setF({...f, vendor_name: e.target.value})} className={cls}/></F>
        <F label="Contact Person"><input value={f.contact_person} onChange={e => setF({...f, contact_person: e.target.value})} className={cls}/></F>
        <F label="Phone"><input value={f.phone} onChange={e => setF({...f, phone: e.target.value})} className={cls}/></F>
        <F label="Email"><input value={f.email} onChange={e => setF({...f, email: e.target.value})} className={cls}/></F>
        <F label="Start date"><DatePicker value={(f.start_date || '').slice(0,10)} onChange={(_v) => setF({...f, start_date: _v})} data-testid="new-amc-start"/></F>
        <F label="End date"><DatePicker value={(f.end_date || '').slice(0,10)} onChange={(_v) => setF({...f, end_date: _v})} data-testid="new-amc-end"/></F>
        <F label="Annual Cost (₹)"><NumberInput value={f.annual_cost} onChange={e => setF({...f, annual_cost: e.target.value})} className={cls}/></F>
        <F label="Coverage" full><input value={f.coverage} onChange={e => setF({...f, coverage: e.target.value})} placeholder="e.g. All AC units, quarterly service + emergency" className={cls}/></F>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="new-amc-submit"/>
    </Modal>
  );
}
